import clsx from 'clsx'
import { t } from 'i18next'

import { ColumnTypes } from '@makyoapp/makyo-sdk/models/Table'
import { ColumnAdapter } from '@makyoapp/makyo-common'
import { SelectDropdown } from '@makyoapp/makyo-ui'

import { filterFnReturnTypes } from '@/core/db/functions/helpers'
import { useActiveLayerContext } from '@/context/__project__/ActiveLayerContext'
import { useDbClientContext } from '@/context/__project__/DbClientContext'
import { useBreakpoint } from '@/hooks/useBreakpoint'
import {
  BuilderComponent,
  BuilderComponentSetting,
  ComponentSettingKey,
  DEFAULT_COLUMN_TYPES,
  FlattenedItem,
} from '@/shared/builder'
import { COLUMN_SELECT_ROW_ID_KEY } from '@/shared/builder-constants'
import { hasResponsiveSettings } from '@/shared/builder/viewport/responsive-settings'
import { SHOW_SEARCH_LIMIT } from '@/shared/constants'
import { RenderColumn } from '@/components/RenderColumn'

interface ColumnSelectFieldSettingProps {
  id: string
  settingId: string
  componentId: string
  value: any
  flattenedComponents: FlattenedItem[]
  comp: BuilderComponent
  ancestors: FlattenedItem[]
  columnSelectSettings: BuilderComponentSetting['columnSelectSettings']
}

const ColumnSelectFieldSetting = ({
  id,
  settingId,
  componentId,
  value,
  comp,
  ancestors,
  columnSelectSettings,
}: ColumnSelectFieldSettingProps) => {
  const { tables, tablesMap } = useDbClientContext()
  const { activeLayerContainer, activeLayerClient } = useActiveLayerContext()
  const { device } = useBreakpoint()

  // find component
  const component = activeLayerClient?.componentsMap.get(componentId)

  const isArr = Array.isArray(value)
  const isForm = comp?.isForm

  let tableSourceId = columnSelectSettings?.tableSourceId
  let formParent: FlattenedItem | undefined

  const bindForm = columnSelectSettings?.bindForm
  const bindDetailScreen = columnSelectSettings?.bindDetailScreen
  const columnTypes = columnSelectSettings?.columnTypes ?? DEFAULT_COLUMN_TYPES
  const multiple = columnSelectSettings?.multiple ?? false
  const dependentKeysOnChange = columnSelectSettings?.dependentKeysOnChange ?? []
  const onValueChangeV2 = columnSelectSettings?.onValueChangeV2
  let includeLookupType = columnSelectSettings?.includeLookupType ?? false

  const NO_UNKNOWN_FN = [
    ComponentSettingKey.ListDataMedia,
    ComponentSettingKey.CardDataThumbnail,
    ComponentSettingKey.CardDataMedia,
  ]

  if (isForm && bindForm) {
    formParent = ancestors.find((a) => a.compId === 'form')
    tableSourceId = ComponentSettingKey.FormDataSource
    includeLookupType = false
  }

  let tableSourceValue: undefined | string | { table: string } = tableSourceId
    ? (activeLayerContainer?.elementSettings[formParent && isForm && bindForm ? formParent.id : componentId]?.[
        tableSourceId
      ] as string | { table: string })
    : undefined

  if (bindDetailScreen) {
    let shouldBind: boolean | 'disabled' = false
    if (typeof bindDetailScreen === 'boolean') {
      shouldBind = bindDetailScreen
    } else {
      const bindDependId = bindDetailScreen.id
      const bindDependValue = bindDependId
        ? activeLayerContainer?.elementSettings[componentId]?.[bindDependId]
        : undefined
      shouldBind = bindDetailScreen.bindWhen(bindDependValue, componentId, ancestors)
    }

    if (shouldBind === true) {
      tableSourceValue = activeLayerContainer?.tableId
    }
  }

  const tbl = typeof tableSourceValue === 'string' ? tableSourceValue : tableSourceValue?.table

  const columns = [...(tablesMap.get(tbl || '')?.GetColumns() || [])]
  if (!bindForm) {
    columns.push({
      id: COLUMN_SELECT_ROW_ID_KEY,
      type: ColumnTypes.RowID,
      name: t('Row ID'),
    } as ColumnAdapter)
  }

  const isValueValid = multiple
    ? Array.isArray(value) && value.every((v) => columns.some((col) => col.id === v))
    : value && columns.some((col) => col.id === value)

  const currentValue = isValueValid ? value : multiple ? [] : ''

  const filteredColumns = columnTypes
    ? columns.filter((c) => {
        // for form component, dont include deferred columns
        if (!bindForm) {
          if (includeLookupType) {
            if (c.type === ColumnTypes.Lookup) {
              return c.resolvedType && columnTypes.includes(c.resolvedType)
            }
          }

          if (c.type === ColumnTypes.Function) {
            return filterFnReturnTypes(c, columnTypes, true)
          }
          if (c.type === ColumnTypes.Math) {
            return [ColumnTypes.Number, ColumnTypes.Math].some((t) => columnTypes.includes(t))
          }
          if (c.type === ColumnTypes.Template) {
            return [ColumnTypes.Text, ColumnTypes.RichText, ColumnTypes.Template].some((t) => columnTypes.includes(t))
          }
          if (c.type === ColumnTypes.SingleCell) {
            return c.resolvedType && columnTypes.includes(c.resolvedType)
          }
        }

        return columnTypes.includes(c.type)
      })
    : columns

  const renderOptions = () => {
    if (multiple) {
      return filteredColumns
    }
    return [
      {
        id: '',
        type: ColumnTypes.Text,
        name: t('Select Column'),
      },
      ...filteredColumns,
    ] as ColumnAdapter[]
  }

  return (
    <SelectDropdown
      withSearch={filteredColumns.length > SHOW_SEARCH_LIMIT}
      id={settingId}
      multiple={multiple}
      value={
        multiple
          ? isArr
            ? columns.filter(({ id }) => value.includes(id))
            : currentValue
              ? [currentValue]
              : []
          : columns.find(({ id }) => id === currentValue)
      }
      onChange={async (opt) => {
        if (!component) return

        if (multiple) {
          const nextValArr = (opt as ColumnAdapter[]).map(({ id }) => id).filter((id) => id !== '')
          component.UpdateSettings(id, nextValArr)
          return
        }

        let updatedColumnName = ''
        const nextVal = (opt as ColumnAdapter).id

        let updatedSettings = component.settings

        if (hasResponsiveSettings(id)) {
          updatedSettings[id] = {
            ...(typeof updatedSettings[id] === 'object' && updatedSettings[id] !== null ? updatedSettings[id] : {}),
            [device]: nextVal,
          }
          component.UpdateSettings(id, updatedSettings[id])
          return
        }

        updatedSettings[id] = nextVal

        // invoke another dependent setting

        // change other dependent keys based on current col value
        if (isForm && dependentKeysOnChange.length > 0) {
          // use existing formParent or find form ancestor if not already set
          const formAncestor = formParent || ancestors.find((a) => a.compId === 'form')
          const formComponent = formAncestor ? activeLayerClient?.componentsMap.get(formAncestor.id) : undefined

          const tableId = formComponent?.settings[ComponentSettingKey.FormDataSource] as string | undefined

          const column = tableId ? tablesMap.get(tableId)?.columnsMap.get(nextVal as string) : undefined

          if (column) {
            updatedColumnName = column._Name

            if (dependentKeysOnChange.length > 0) {
              dependentKeysOnChange.forEach((key) => {
                updatedSettings[key] = column._Name
              })
            }
          }
        }

        if (onValueChangeV2) {
          onValueChangeV2(nextVal, updatedSettings)
        }

        // update component
        component.Update(updatedColumnName ? { _Title: updatedColumnName } : {}, updatedSettings)
      }}
      options={renderOptions()}
      optionLabel={(opt) => `${(opt as ColumnAdapter).id} ${(opt as ColumnAdapter).name}`}
      renderOption={(opt) => (
        <RenderColumn
          withStyle={false}
          col={opt as ColumnAdapter}
          className={clsx((opt as ColumnAdapter)?.id === '' && 'text-light')}
        />
      )}
    />
  )
}

export default ColumnSelectFieldSetting
